// features/products/pages/ProductVariantsPage.tsx
/**
 * Product Variants Page - Standalone Variant Management
 * 
 * Full variant management with:
 * - Inline editing (click to edit in row)
 * - Modal form for adding new variants
 * - Bulk actions (delete selected)
 * - Status toggles
 * - Stock quantity display
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProduct, addProductVariant, updateProductVariant, deleteProductVariant,
    ProductData, ProductVariantData,
} from '../services/productsApi';
import {
    ArrowLeft, Plus, Trash2, Pencil, CheckCircle, X,
    Save, Boxes, Barcode, DollarSign, Package,
    AlertTriangle, Copy, Eye, EyeOff,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

export function ProductVariantsPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ProductVariantData>>({});
    const [newForm, setNewForm] = useState({
        name: '', sku: '', barcode: '',
        retailPrice: '', wholesalePrice: '', clearancePrice: '',
        costPrice: '', stockQuantity: '0',
    });
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    const { data: product, isLoading, isError, refetch } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const addMutation = useMutation({
        mutationFn: () => addProductVariant(user!.tenantId, productId!, newForm),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            setShowAddModal(false);
            setNewForm({ name: '', sku: '', barcode: '', retailPrice: '', wholesalePrice: '', clearancePrice: '', costPrice: '', stockQuantity: '0' });
            addToast({ type: 'success', title: 'Variant added' });
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ variantId, data }: { variantId: string; data: Record<string, unknown> }) =>
            updateProductVariant(user!.tenantId, variantId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            setEditingId(null);
            setEditForm({});
            addToast({ type: 'success', title: 'Variant updated' });
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (variantId: string) => deleteProductVariant(user!.tenantId, variantId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            setDeleteConfirm(null);
            setSelectedIds(prev => { const next = new Set(prev); next.delete(deleteConfirm || ''); return next; });
            addToast({ type: 'success', title: 'Variant deleted' });
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const variants = product?.variants || [];
    const formatNaira = (val: string | null | undefined) =>
        val ? `₦${parseFloat(val).toLocaleString()}` : '—';

    const startEdit = (variant: ProductVariantData) => {
        setEditingId(variant.id);
        setEditForm({
            name: variant.name,
            sku: variant.sku,
            barcode: variant.barcode || '',
            retailPrice: variant.retailPrice || '',
            wholesalePrice: variant.wholesalePrice || '',
            clearancePrice: variant.clearancePrice || '',
            costPrice: variant.costPrice || '',
            stockQuantity: variant.stockQuantity || '0',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const saveEdit = (variantId: string) => {
        updateMutation.mutate({ variantId, data: editForm as Record<string, unknown> });
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === variants.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(variants.map(v => v.id)));
        }
    };

    const bulkDelete = () => {
        if (confirm(`Delete ${selectedIds.size} selected variants?`)) {
            selectedIds.forEach(id => deleteMutation.mutate(id));
            setSelectedIds(new Set());
        }
    };

    const duplicateVariant = (variant: ProductVariantData) => {
        setNewForm({
            name: `${variant.name} (Copy)`,
            sku: `${variant.sku}-COPY`,
            barcode: '',
            retailPrice: variant.retailPrice || '',
            wholesalePrice: variant.wholesalePrice || '',
            clearancePrice: variant.clearancePrice || '',
            costPrice: variant.costPrice || '',
            stockQuantity: '0',
        });
        setShowAddModal(true);
    };

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (isError || !product) return <ErrorState title="Product not found" onRetry={() => refetch()} />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate(`/products/${productId}`)} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg">
                                <Boxes className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">{product.name}</h1>
                                <p className="text-sm text-slate-500">{variants.length} variant{variants.length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg text-sm"
                            type="button"
                        >
                            <Plus className="w-4 h-4" /> Add Variant
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
                {/* Bulk Actions */}
                <AnimatePresence>
                    {selectedIds.size > 0 && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                            className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-3">
                            <span className="text-sm font-semibold text-rose-700">{selectedIds.size} selected</span>
                            <button onClick={bulkDelete} className="px-3 py-1.5 text-xs font-semibold text-rose-700 bg-white border border-rose-200 rounded-lg hover:bg-rose-100"
                                type="button"><Trash2 className="w-3.5 h-3.5 inline mr-1" />Delete Selected</button>
                            <button onClick={() => setSelectedIds(new Set())} className="ml-auto p-1 text-slate-400" type="button"><X className="w-4 h-4" /></button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {variants.length === 0 ? (
                    <EmptyState
                        icon={<Boxes className="w-16 h-16" />}
                        title="No Variants"
                        description="This product doesn't have any variants yet. Add size, color, or flavor variations."
                        action={{ label: 'Add First Variant', onClick: () => setShowAddModal(true) }}
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 w-10">
                                            <button onClick={toggleSelectAll} className="text-slate-300 hover:text-emerald-500" type="button">
                                                {selectedIds.size === variants.length ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <div className="w-5 h-5 rounded border-2 border-slate-300" />}
                                            </button>
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">SKU</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Barcode</th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Retail Price</th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Stock</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase w-32">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {variants.map((variant) => {
                                        const isEditing = editingId === variant.id;
                                        return (
                                            <tr key={variant.id} className={clsx('group', isEditing && 'bg-blue-50/30')}>
                                                <td className="px-4 py-3">
                                                    <button onClick={() => toggleSelect(variant.id)} type="button">
                                                        {selectedIds.has(variant.id) ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <div className="w-5 h-5 rounded border-2 border-slate-300 group-hover:border-emerald-400" />}
                                                    </button>
                                                </td>
                                                {isEditing ? (
                                                    <>
                                                        <td className="px-4 py-2"><input type="text" value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-2 py-1.5 border rounded-lg text-sm" /></td>
                                                        <td className="px-4 py-2"><input type="text" value={editForm.sku || ''} onChange={e => setEditForm({ ...editForm, sku: e.target.value })} className="w-full px-2 py-1.5 border rounded-lg text-sm font-mono" /></td>
                                                        <td className="px-4 py-2"><input type="text" value={editForm.barcode || ''} onChange={e => setEditForm({ ...editForm, barcode: e.target.value })} className="w-full px-2 py-1.5 border rounded-lg text-sm font-mono" /></td>
                                                        <td className="px-4 py-2"><input type="number" step="0.01" value={editForm.retailPrice || ''} onChange={e => setEditForm({ ...editForm, retailPrice: e.target.value })} className="w-24 px-2 py-1.5 border rounded-lg text-sm text-right" /></td>
                                                        <td className="px-4 py-2"><input type="number" value={editForm.stockQuantity || ''} onChange={e => setEditForm({ ...editForm, stockQuantity: e.target.value })} className="w-20 px-2 py-1.5 border rounded-lg text-sm text-right" /></td>
                                                        <td className="px-4 py-2 text-center">
                                                            <button onClick={() => setEditForm({ ...editForm, isActive: !(editForm as any).isActive })} type="button">
                                                                <Badge variant={(editForm as any).isActive !== false ? 'success' : 'danger'} size="sm">{(editForm as any).isActive !== false ? 'Active' : 'Inactive'}</Badge>
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-2 text-right">
                                                            <div className="flex items-center justify-end gap-1">
                                                                <button onClick={() => saveEdit(variant.id)} className="p-1.5 bg-emerald-500 text-white rounded-lg" type="button"><Save className="w-3.5 h-3.5" /></button>
                                                                <button onClick={cancelEdit} className="p-1.5 bg-slate-200 rounded-lg" type="button"><X className="w-3.5 h-3.5" /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{variant.name}</td>
                                                        <td className="px-4 py-3 text-sm font-mono text-slate-500">{variant.sku}</td>
                                                        <td className="px-4 py-3 text-sm font-mono text-slate-400">{variant.barcode || '—'}</td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-right">{formatNaira(variant.retailPrice)}</td>
                                                        <td className="px-4 py-3 text-sm text-right">{parseFloat(variant.stockQuantity || '0').toLocaleString()}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <Badge variant={variant.isActive ? 'success' : 'danger'} size="sm">{variant.isActive ? 'Active' : 'Inactive'}</Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => startEdit(variant)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600" type="button" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                                                <button onClick={() => duplicateVariant(variant)} className="p-1.5 rounded-lg hover:bg-purple-50 text-slate-400 hover:text-purple-600" type="button" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                                                                <button onClick={() => setDeleteConfirm(variant.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600" type="button" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>

            {/* Add Variant Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 text-white flex items-center justify-between sticky top-0 rounded-t-3xl">
                                <div className="flex items-center gap-3"><Boxes className="w-5 h-5" /><h2 className="text-lg font-bold">Add Variant</h2></div>
                                <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg bg-white/20"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Name *</label><input type="text" value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" placeholder="e.g., 35cl" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">SKU *</label><input type="text" value={newForm.sku} onChange={e => setNewForm({ ...newForm, sku: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm font-mono" placeholder="VAR-001" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Barcode</label><input type="text" value={newForm.barcode} onChange={e => setNewForm({ ...newForm, barcode: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm font-mono" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Stock Quantity</label><input type="number" value={newForm.stockQuantity} onChange={e => setNewForm({ ...newForm, stockQuantity: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Retail Price</label><input type="number" step="0.01" value={newForm.retailPrice} onChange={e => setNewForm({ ...newForm, retailPrice: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Wholesale Price</label><input type="number" step="0.01" value={newForm.wholesalePrice} onChange={e => setNewForm({ ...newForm, wholesalePrice: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Cost Price</label><input type="number" step="0.01" value={newForm.costPrice} onChange={e => setNewForm({ ...newForm, costPrice: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Clearance Price</label><input type="number" step="0.01" value={newForm.clearancePrice} onChange={e => setNewForm({ ...newForm, clearancePrice: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button onClick={() => setShowAddModal(false)} className="px-5 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                    <button onClick={() => addMutation.mutate()} disabled={!newForm.name || !newForm.sku || addMutation.isPending}
                                        className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" type="button">
                                        {addMutation.isPending ? 'Adding...' : 'Add Variant'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                            <Trash2 className="w-12 h-12 text-rose-500 mx-auto mb-3" />
                            <h3 className="text-lg font-bold">Delete Variant?</h3>
                            <p className="text-sm text-slate-500 mt-2">This action cannot be undone.</p>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => deleteMutation.mutate(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Delete</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}